// This file is auto-generated by @hey-api/openapi-ts

import type { Options as ClientOptions, TDataShape, Client } from './client'
import type {
  GetNavigationData,
  GetNavigationResponses,
  GetDriveStatisticsData,
  GetDriveStatisticsResponses,
  GetDataSyncData,
  GetDataSyncResponses,
  GetGpsData,
  GetGpsResponses,
  GetSystemStatusData,
  GetSystemStatusResponses,
  ReportUnexpectedEventData,
  ReportUnexpectedEventResponses,
  ReportStartedData,
  ReportStartedResponses,
  ReportFinishedData,
  ReportFinishedResponses,
  GetRawPhotosData,
  GetRawPhotosResponses,
  GetPhotoData,
  GetPhotoResponses,
  GetThumbnailByUuidData,
  GetThumbnailByUuidResponses,
  GetEvidencePackageByUuidData,
  GetEvidencePackageByUuidResponses,
  GetPhotosData,
  GetPhotosResponses,
  GetPhotosThumbnailData,
  GetPhotosThumbnailResponses,
  GetPanoramaPreviewBase64Data,
  GetPanoramaPreviewBase64Responses,
  GetRawPhotoData,
  GetRawPhotoResponses,
  GetPhotos3dData,
  GetPhotos3dResponses,
  ChangeCameraModeData,
  ChangeCameraModeResponses,
  PauseData,
  PauseResponses,
  UnpauseData,
  UnpauseResponses,
  SkipSegmentData,
  SkipSegmentResponses,
  GetDriversData,
  GetDriversResponses,
  DriverSignInData,
  DriverSignInResponses,
  DriverSignOutData,
  DriverSignOutResponses,
  GetGoToGarageData,
  GetGoToGarageResponses,
  PutGoToGarageData,
  PutGoToGarageResponses,
  GetDisplayableEventsData,
  GetDisplayableEventsResponses,
  DirectionsData,
  DirectionsResponses,
} from './types.gen'
import { client as _heyApiClient } from './@tanstack/initializer'

export type Options<TData extends TDataShape = TDataShape, ThrowOnError extends boolean = boolean> = ClientOptions<
  TData,
  ThrowOnError
> & {
  /**
   * You can provide a client instance returned by `createClient()` instead of
   * individual options. This might be also useful if you want to implement a
   * custom client.
   */
  client?: Client
  /**
   * You can pass arbitrary values through the `meta` object. This can be
   * used to access values that aren't defined as part of the SDK function.
   */
  meta?: Record<string, unknown>
}

/**
 * Fetch Navigation Data
 */
export const getNavigation = <ThrowOnError extends boolean = false>(
  options?: Options<GetNavigationData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetNavigationResponses, unknown, ThrowOnError>({
    url: '/navigation',
    ...options,
  })
}

/**
 * Fetch drive statistics for current drive
 */
export const getDriveStatistics = <ThrowOnError extends boolean = false>(
  options?: Options<GetDriveStatisticsData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetDriveStatisticsResponses, unknown, ThrowOnError>({
    url: '/drive-statistics',
    ...options,
  })
}

/**
 * Fetch Data Sync state
 */
export const getDataSync = <ThrowOnError extends boolean = false>(options?: Options<GetDataSyncData, ThrowOnError>) => {
  return (options?.client ?? _heyApiClient).get<GetDataSyncResponses, unknown, ThrowOnError>({
    url: '/data-sync',
    ...options,
  })
}

/**
 * Get current GPS coordinates
 */
export const getGps = <ThrowOnError extends boolean = false>(options?: Options<GetGpsData, ThrowOnError>) => {
  return (options?.client ?? _heyApiClient).get<GetGpsResponses, unknown, ThrowOnError>({
    url: '/gps',
    ...options,
  })
}

/**
 * Get car system status
 */
export const getSystemStatus = <ThrowOnError extends boolean = false>(
  options?: Options<GetSystemStatusData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetSystemStatusResponses, unknown, ThrowOnError>({
    url: '/system-status',
    ...options,
  })
}

/**
 * Report an unexpected event (e.g., blocked road)
 */
export const reportUnexpectedEvent = <ThrowOnError extends boolean = false>(
  options: Options<ReportUnexpectedEventData, ThrowOnError>
) => {
  return (options.client ?? _heyApiClient).post<ReportUnexpectedEventResponses, unknown, ThrowOnError>({
    url: '/unexpected-event',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })
}

/**
 * Triggered by the driver to indicate that the vehicle is at the beginning of the route and about to start the drive.
 * Before starting the drive, a check of the camera systems should be performed.
 */
export const reportStarted = <ThrowOnError extends boolean = false>(
  options?: Options<ReportStartedData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).post<ReportStartedResponses, unknown, ThrowOnError>({
    url: '/report-started',
    ...options,
  })
}

/**
 * Report that the requested drive is finished (triggered by the driver)
 * When the drive is marked as finished, the navigation endpoint should start returning a different one.
 */
export const reportFinished = <ThrowOnError extends boolean = false>(
  options?: Options<ReportFinishedData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).post<ReportFinishedResponses, unknown, ThrowOnError>({
    url: '/report-finished',
    ...options,
  })
}

/**
 * Retrieve a raw unenhanced photo with captures from all the cameras to confirm that the systems are operational.
 */
export const getRawPhotos = <ThrowOnError extends boolean = false>(
  options?: Options<GetRawPhotosData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetRawPhotosResponses, unknown, ThrowOnError>({
    url: '/raw-photos',
    ...options,
  })
}

/**
 * Retrieve a raw photo from a single camera.
 */
export const getPhoto = <ThrowOnError extends boolean = false>(options: Options<GetPhotoData, ThrowOnError>) => {
  return (options.client ?? _heyApiClient).get<GetPhotoResponses, unknown, ThrowOnError>({
    url: '/raw-photo-hq/{camera}',
    ...options,
  })
}

/**
 * Retrieve a thumbnail image of evidence-package.
 */
export const getThumbnailByUuid = <ThrowOnError extends boolean = false>(
  options: Options<GetThumbnailByUuidData, ThrowOnError>
) => {
  return (options.client ?? _heyApiClient).get<GetThumbnailByUuidResponses, unknown, ThrowOnError>({
    url: '/thumbnail/{uuid}',
    ...options,
  })
}

/**
 * Retrieve a .7z archive of evidence-package.
 */
export const getEvidencePackageByUuid = <ThrowOnError extends boolean = false>(
  options: Options<GetEvidencePackageByUuidData, ThrowOnError>
) => {
  return (options.client ?? _heyApiClient).get<GetEvidencePackageByUuidResponses, unknown, ThrowOnError>({
    url: '/evidence-package/{uuid}',
    ...options,
  })
}

/**
 * Retrieve a photo with captures from all cameras to confirm that the systems are operational.
 */
export const getPhotos = <ThrowOnError extends boolean = false>(options?: Options<GetPhotosData, ThrowOnError>) => {
  return (options?.client ?? _heyApiClient).get<GetPhotosResponses, unknown, ThrowOnError>({
    url: '/photos',
    ...options,
  })
}

/**
 * Retrieve thumbnail photos from all cameras
 */
export const getPhotosThumbnail = <ThrowOnError extends boolean = false>(
  options?: Options<GetPhotosThumbnailData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetPhotosThumbnailResponses, unknown, ThrowOnError>({
    url: '/photos-thumbnail',
    ...options,
  })
}

/**
 * Retrieve a panorama preview base64 image with timestamp metadata
 */
export const getPanoramaPreviewBase64 = <ThrowOnError extends boolean = false>(
  options?: Options<GetPanoramaPreviewBase64Data, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetPanoramaPreviewBase64Responses, unknown, ThrowOnError>({
    url: '/panorama-preview-base64',
    ...options,
  })
}

/**
 * Retrieve a photo from a single camera.
 */
export const getRawPhoto = <ThrowOnError extends boolean = false>(options: Options<GetRawPhotoData, ThrowOnError>) => {
  return (options.client ?? _heyApiClient).get<GetRawPhotoResponses, unknown, ThrowOnError>({
    url: '/photo-hq/{camera}',
    ...options,
  })
}

/**
 * Retrieve a range-photo with captures from all 3D cameras to confirm that the systems are operational.
 */
export const getPhotos3D = <ThrowOnError extends boolean = false>(options?: Options<GetPhotos3dData, ThrowOnError>) => {
  return (options?.client ?? _heyApiClient).get<GetPhotos3dResponses, unknown, ThrowOnError>({
    url: '/photos-3d',
    ...options,
  })
}

/**
 * Change camera mode to the passed one
 * When this endpoint is invoked it sets the camera mode to either day or night based on the mode specified.
 */
export const changeCameraMode = <ThrowOnError extends boolean = false>(
  options: Options<ChangeCameraModeData, ThrowOnError>
) => {
  return (options.client ?? _heyApiClient).post<ChangeCameraModeResponses, unknown, ThrowOnError>({
    url: '/change-camera-mode',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })
}

/**
 * Pause segment processing.
 * When this endpoint is invoked all passed segments will be ignored until unpaused
 */
export const pause = <ThrowOnError extends boolean = false>(options?: Options<PauseData, ThrowOnError>) => {
  return (options?.client ?? _heyApiClient).post<PauseResponses, unknown, ThrowOnError>({
    url: '/pause',
    ...options,
  })
}

/**
 * Unpause segment processing.
 * When this endpoint is invoked all passed segments will processed again
 */
export const unpause = <ThrowOnError extends boolean = false>(options?: Options<UnpauseData, ThrowOnError>) => {
  return (options?.client ?? _heyApiClient).post<UnpauseResponses, unknown, ThrowOnError>({
    url: '/unpause',
    ...options,
  })
}

/**
 * Skip current segment to the following one
 * When this endpoint is invoked the current segment is moved to another one
 */
export const skipSegment = <ThrowOnError extends boolean = false>(options?: Options<SkipSegmentData, ThrowOnError>) => {
  return (options?.client ?? _heyApiClient).post<SkipSegmentResponses, unknown, ThrowOnError>({
    url: '/skip-segment',
    ...options,
  })
}

/**
 * Retrieve a list of available drivers
 */
export const getDrivers = <ThrowOnError extends boolean = false>(options?: Options<GetDriversData, ThrowOnError>) => {
  return (options?.client ?? _heyApiClient).get<GetDriversResponses, unknown, ThrowOnError>({
    url: '/drivers',
    ...options,
  })
}

/**
 * Sign in as a driver and start a work period
 */
export const driverSignIn = <ThrowOnError extends boolean = false>(
  options: Options<DriverSignInData, ThrowOnError>
) => {
  return (options.client ?? _heyApiClient).post<DriverSignInResponses, unknown, ThrowOnError>({
    url: '/driver-sign-in',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })
}

/**
 * Sign a driver out and terminate a work period
 */
export const driverSignOut = <ThrowOnError extends boolean = false>(
  options?: Options<DriverSignOutData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).post<DriverSignOutResponses, unknown, ThrowOnError>({
    url: '/driver-sign-out',
    ...options,
  })
}

/**
 * Get go-to-garage flag
 */
export const getGoToGarage = <ThrowOnError extends boolean = false>(
  options?: Options<GetGoToGarageData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetGoToGarageResponses, unknown, ThrowOnError>({
    url: '/go-to-garage',
    ...options,
  })
}

/**
 * Set/unset go-to-garage flag
 */
export const putGoToGarage = <ThrowOnError extends boolean = false>(
  options: Options<PutGoToGarageData, ThrowOnError>
) => {
  return (options.client ?? _heyApiClient).put<PutGoToGarageResponses, unknown, ThrowOnError>({
    url: '/go-to-garage',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })
}

/**
 * Retrieve displayable evidence data for the current drive
 * Returns evidence data including signpost and vehicle detection coordinates that can be displayed to the driver during navigation. This includes locations where signposts and vehicles were detected along the route.
 */
export const getDisplayableEvents = <ThrowOnError extends boolean = false>(
  options?: Options<GetDisplayableEventsData, ThrowOnError>
) => {
  return (options?.client ?? _heyApiClient).get<GetDisplayableEventsResponses, unknown, ThrowOnError>({
    url: '/displayable-events',
    ...options,
  })
}

/**
 * Get directions from Google Route API
 * When this endpoint is invoked it ask the CitySense API to fetch directions from Google Routes API
 */
export const directions = <ThrowOnError extends boolean = false>(options: Options<DirectionsData, ThrowOnError>) => {
  return (options.client ?? _heyApiClient).post<DirectionsResponses, unknown, ThrowOnError>({
    url: '/directions',
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })
}
